export const EQUIPMENT_TAG_VARIANTS = {
  RED: "red",
  YELLOW: "ylw",
  BLUE: "blue",
} as const;

export type EquipmentTagVariant =
  (typeof EQUIPMENT_TAG_VARIANTS)[keyof typeof EQUIPMENT_TAG_VARIANTS];

interface OperationalCodeEntry {
  code?: string | null;
}

interface OperationalInfoEntry {
  operationalCode?: string | null;
}

interface ActiveVisitReasonTagSource {
  loadIntegrityIssueList?: ReadonlyArray<unknown> | null;
}

export interface EquipmentTagSource {
  operationalCodes?: ReadonlyArray<string | OperationalCodeEntry> | null;
  operationalInfo?: ReadonlyArray<OperationalInfoEntry> | null;
  visitDetails?: {
    activeVisitReasons?: ReadonlyArray<ActiveVisitReasonTagSource | null> | null;
  } | null;
}

const DAMAGED_SEVERE = "DAMAGED_SEVERE";
const DAMAGED_MODERATE = "DAMAGED_MODERATE";
const PREVENTIVE_MAINTENANCE = "PREVENTIVE_MAINTENANCE";

const extractOperationalCodes = (equipment: EquipmentTagSource): Set<string> => {
  const flatCodes = equipment.operationalCodes;
  const sourceCodes = Array.isArray(flatCodes)
    ? flatCodes.map((entry) => (typeof entry === "string" ? entry : entry.code))
    : (equipment.operationalInfo ?? []).map((entry) => entry.operationalCode);

  return new Set(
    sourceCodes
      .filter((code): code is string => typeof code === "string" && code.length > 0)
      .map((code) => code.toUpperCase())
  );
};

export const deriveEquipmentTagVariants = (
  equipment: EquipmentTagSource | null | undefined
): EquipmentTagVariant[] => {
  if (!equipment) return [];

  const codes = extractOperationalCodes(equipment);
  const variants: EquipmentTagVariant[] = [];

  if (codes.has(DAMAGED_SEVERE)) variants.push(EQUIPMENT_TAG_VARIANTS.RED);
  if (codes.has(DAMAGED_MODERATE) || codes.has(PREVENTIVE_MAINTENANCE)) {
    variants.push(EQUIPMENT_TAG_VARIANTS.YELLOW);
  }

  const isBlueFlagged = (equipment.visitDetails?.activeVisitReasons ?? []).some(
    (reason) => (reason?.loadIntegrityIssueList?.length ?? 0) > 0
  );
  if (isBlueFlagged) variants.push(EQUIPMENT_TAG_VARIANTS.BLUE);

  return variants;
};
